import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { confirm } from '@/stores/confirmStore';
import { useCompanyStore } from '../../../../stores/companyStore';
import { useQuoteEdit, QuoteEditData } from '../../../../hooks/useQuoteEdit';
import { useTaxRates } from '../../../../hooks/useTaxRates';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { isVatExempt } from '../../../../utils/vatCalculations';
import { EditableItemsTable } from '../../shared/editable/EditableItemsTable';
import { EditableClientSection } from '../../shared/editable/EditableClientSection';
import { EditableNotesSection } from '../../shared/editable/EditableNotesSection';
import { EditableDiscountSection } from '../../shared/editable/EditableDiscountSection';
import { EditActionButtons } from '../../shared/editable/EditActionButtons';
import { CompanyInfoSection } from '../../shared/CompanyInfoSection';
import { TotalsSection } from '../../shared/TotalsSection';
import { LegalFooter } from '../../shared/LegalFooter';
import { ProductSelector } from '@/features/catalog/components/ProductSelector';
import { Dashboard } from '../../../Dashboard';
import { EditableQuoteExtendedFields } from '../../shared/editable/EditableQuoteExtendedFields';
import type { TemplateSettings, Company } from '../../types';

interface QuoteTemplateElegantEditProps {
  initialData: QuoteEditData;
  template: TemplateSettings;
  company: Company | null;
  color: string;
}

/**
 * Template Élégant (BUSINESS) - Mode édition
 * - Logo: top-center
 * - Style: luxueux avec bordures dorées
 * - Table: bordered
 */
export const QuoteTemplateElegantEdit: React.FC<QuoteTemplateElegantEditProps> = ({
  initialData,
  template,
  company,
  color: colorProp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company: companyStore } = useCompanyStore();
  const finalCompany = company || companyStore;
  const quoteColor = colorProp || template?.primaryColor || '#D4AF37';
  const secondaryColor = template?.secondaryColor || '#1F2937';

  const vatRegime = finalCompany?.vatRegime;
  const isExempt = isVatExempt(vatRegime);

  // ✅ Charger les TaxRates une seule fois au niveau du composant parent
  const { taxRates, loading: loadingTaxRates } = useTaxRates();
  const { features: companyFeatures } = useCompanyFeatures();

  const {
    quoteData,
    isSaving,
    hasChanges,
    updateField,
    updateDiscountType,
    updateDiscountValue,
    updateItem,
    addItem,
    removeItem,
    addSectionTitle,
    addComment,
    addSubtotal,
    addProductFromCatalog,
    saveQuote,
  } = useQuoteEdit(
    initialData,
    vatRegime
  );

  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  const handleCancel = async () => {
    if (hasChanges) {
      const confirmed = await confirm({
        title: 'Modifications non sauvegardées',
        message: 'Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?',
        variant: 'warning',
        confirmText: 'Quitter',
        cancelText: 'Annuler',
      });
      if (!confirmed) return;
    }
    navigate('/quotes');
  };

  const handleVisualize = () => {
    if (quoteData.id) {
      navigate(`/quotes/${quoteData.id}`);
    }
  };

  const handleSelectClient = (client: any) => {
    if (!client) return;

    // ✅ CRITIQUE: Capturer le clientId (obligatoire pour backend)
    updateField('clientId', client.id);
    updateField('clientName', client.fullName || client.companyName || '');
    updateField('clientCompany', client.companyName || '');
    updateField('clientEmail', client.email || '');
    updateField('clientPhone', client.phone || '');
    updateField('clientAddressLine1', client.addressLine1 || client.address || '');
    updateField('clientCity', client.city || '');
    updateField('clientZipCode', client.zipCode || client.postalCode || '');
    updateField('clientCountry', client.countryCode || client.country || 'FR');
  };

  // Convertir les données pour le Dashboard (format imbriqué attendu)
  const convertToQuoteDataForDashboard = (): any => ({
    number: quoteData.quoteNumber,
    issueDate: quoteData.date,
    validUntil: quoteData.validUntil,
    company: {
      legalName: finalCompany?.legalName || finalCompany?.tradeName || '',
      addressLine1: finalCompany?.addressLine1 || '',
      city: finalCompany?.city || '',
      zipCode: finalCompany?.zipCode || '',
      countryCode: finalCompany?.taxCountryCode || 'FR',
      phone: finalCompany?.phone || '',
      email: finalCompany?.email || '',
    },
    client: {
      fullName: quoteData.clientName || '',
      companyName: quoteData.clientCompany || '',
      addressLine1: quoteData.clientAddressLine1 || '',
      city: quoteData.clientCity || '',
      zipCode: quoteData.clientZipCode || '',
      countryCode: 'FR',
      phone: quoteData.clientPhone || '',
      email: quoteData.clientEmail || '',
    },
    items: quoteData.items,
    eventTitle: quoteData.title,  // ✅ Aligné sur Prisma
    internalNotes: quoteData.internalNotes,
    termsConditions: quoteData.termsConditions || '',
    discountType: quoteData.discountType,
    discountValue: quoteData.discountValue,
    discountAmount: quoteData.discountAmount,
  });

  // Ventilation TVA déléguée à la section Totaux

  return (
    <div className="min-h-screen bg-transparent">
      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div
          className="bg-white/20 backdrop-blur-xl shadow-2xl border-2 rounded-3xl"
          style={{ borderColor: quoteColor }}
        >
          <div className="p-6 md:p-10">
            {/* Header Elegant - Logo CENTRÉ avec lignes décoratives */}
            <div className="text-center mb-8 border-b-2 pb-6" style={{ borderColor: quoteColor }}>
              <CompanyInfoSection
                company={finalCompany}
                companyName={quoteData.companyName}
                companyAddress={quoteData.companyAddressLine1}
                companyCity={quoteData.companyCity}
                companyPostalCode={quoteData.companyZipCode}
                companyPhone={quoteData.companyPhone}
                companyEmail={quoteData.companyEmail}
                logoPosition="top-center"
              />
            </div>

            {/* DEVIS Badge - Elegant style (centré) */}
            <div className="text-center mb-8">
              <div className="inline-block">
                <div className="border-4 px-6 py-3 rounded-2xl bg-white/10 backdrop-blur-sm" style={{ borderColor: quoteColor }}>
                  <h3
                    className="text-xs font-bold uppercase tracking-widest"
                    style={{ color: secondaryColor }}
                  >
                    Devis
                  </h3>
                  <span className="text-xl md:text-2xl font-bold" style={{ color: quoteColor }}>
                    {quoteData.quoteNumber?.replace(/^DEVIS-/i, '') || quoteData.quoteNumber}
                  </span>
                </div>
              </div>
            </div>

            {/* Quote Info Grid - Dates et Client */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Left: Dates éditables */}
              <div className="border-l-4 pl-4 bg-white/10 backdrop-blur-sm rounded-xl p-4" style={{ borderColor: quoteColor }}>
                <div className="space-y-3 text-sm">
                  <div>
                    <label
                      className="block font-semibold mb-1"
                      style={{ color: secondaryColor }}
                    >
                      Date :
                    </label>
                    <input
                      type="date"
                      value={quoteData.date}
                      onChange={(e) => updateField('date', e.target.value)}
                      className="w-full px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label
                      className="block font-semibold mb-1"
                      style={{ color: secondaryColor }}
                    >
                      Valable jusqu'au :
                    </label>
                    <input
                      type="date"
                      value={quoteData.validUntil}
                      onChange={(e) => updateField('validUntil', e.target.value)}
                      className="w-full px-3 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                    />
                  </div>
                </div>
              </div>

              {/* Right: Client éditable */}
              <div className="border-l-4 pl-4 bg-white/10 backdrop-blur-sm rounded-xl p-4" style={{ borderColor: quoteColor }}>
                <EditableClientSection
                  data={quoteData}
                  onUpdate={updateField}
                  onSelectClient={handleSelectClient}
                  accentColor={quoteColor}
                />
              </div>
            </div>

            {/* Titre du document (éditable) - Style Elegant avec bordure dorée */}
            <div className="mb-6">
              <div className="border-l-4 pl-4" style={{ borderColor: quoteColor }}>
                <input
                  type="text"
                  value={quoteData.title || ''}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder={t('templates.quote.subjectPlaceholder')}
                  className="w-full px-3 py-2 text-lg font-semibold bg-white/10 backdrop-blur-sm border border-white/40 rounded-lg focus:ring-2 focus:bg-white/30 transition-all duration-300 placeholder:text-gray-400 placeholder:font-normal"
                  style={{ color: quoteColor }}
                />
              </div>
            </div>

            {/* Tableau des items éditable - Style bordered */}
            <EditableItemsTable
              items={quoteData.items}
              onUpdateItem={updateItem}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              onAddFromCatalog={() => setShowProductSelector(true)}
              tableStyle="bordered"
              accentColor={quoteColor}
              showVat={!isExempt}
              taxRates={taxRates}
              loadingTaxRates={loadingTaxRates}
              onAddSectionTitle={addSectionTitle}
              onAddComment={addComment}
              onAddSubtotal={addSubtotal}
              features={{
                showReference: companyFeatures?.referenceSKU ?? true,
                showUnit: companyFeatures?.unitOfMeasure ?? true,
                showDiscount: companyFeatures?.lineDiscount ?? true,
                showSections: companyFeatures?.sections ?? true,
              }}
            />

            {/* Section Remise éditable */}
            <div className="mt-6">
              <EditableDiscountSection
                data={quoteData}
                onUpdateType={updateDiscountType}
                onUpdateValue={updateDiscountValue}
                accentColor={quoteColor}
              />
            </div>

            {/* Section Totaux */}
            <TotalsSection
              subtotal={quoteData.subtotal}
              tax={quoteData.tax}
              total={quoteData.total}
              discount={quoteData.discountAmount}
              discountType={quoteData.discountType}
              discountValue={quoteData.discountValue}
              accentColor={quoteColor}
              showVatBreakdown={!isExempt}
            />

            {/* Section Notes éditable */}
            <div className="mt-8">
              <EditableNotesSection
                data={quoteData}
                onUpdate={updateField}
                accentColor={quoteColor}
              />
            </div>

            {/* ✅ AJOUTÉ: Champs étendus (workflow, signature, multi-devise) */}
            <div className="mt-8">
              <EditableQuoteExtendedFields
                data={quoteData}
                onUpdate={updateField}
                accentColor={quoteColor}
              />
            </div>

            {/* Footer légal */}
            <LegalFooter company={finalCompany} notes={quoteData.termsConditions} />
          </div>
        </div>
      </div>

      {/* Dashboard Modal */}
      {showDashboard && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[55] flex items-center justify-center p-2 md:p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 w-full md:max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-white/30 px-4 md:px-6 py-3 md:py-4 flex justify-between items-center">
              <h2 className="text-lg md:text-xl font-bold text-gray-800">
                Dashboard de Gestion
              </h2>
              <button
                onClick={() => setShowDashboard(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl flex-shrink-0 ml-2 p-2 hover:bg-white/50 rounded-xl transition-all duration-300"
              >
                ×
              </button>
            </div>
            <div className="p-4 md:p-6">
              <Dashboard
                quoteData={convertToQuoteDataForDashboard()}
                onUpdateQuote={() => {}}
                onSave={saveQuote}
              />
            </div>
          </div>
        </div>
      )}

      {/* Product Selector Modal */}
      {showProductSelector && (
        <ProductSelector
          onSelectProduct={async (product: any) => {
            await addProductFromCatalog(product);
            setShowProductSelector(false);
          }}
          onClose={() => setShowProductSelector(false)}
        />
      )}

      {/* Boutons d'actions flottants */}
      <EditActionButtons
        onSave={saveQuote}
        onCancel={handleCancel}
        onShowDashboard={() => setShowDashboard(true)}
        onVisualize={handleVisualize}
        isSaving={isSaving}
        hasChanges={hasChanges}
        accentColor={quoteColor}
        documentType="quote"
      />
    </div>
  );
};
